import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Target, Award, Trophy, Flame, Medal, Gift, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AIGenerateButton, AIGenerateModal, AIGenerateContext } from '@/components/ai';
import { useAIGenerate } from '@/hooks/useAIGenerate';

interface MilestoneBuilderProps {
  companyId: string;
}

const MILESTONE_ICONS = ['🎯', '🏆', '⭐', '🚀', '💎', '👑', '🎖️', '🌟', '💪', '🔥'];

const MILESTONE_TYPES = [
  { value: 'total_sessions', label: 'Total Sessions Completed' },
  { value: 'avg_score', label: 'Average Score Achieved' },
  { value: 'total_badges', label: 'Badges Earned' },
  { value: 'cpd_points', label: 'CPD Points Earned' },
  { value: 'streak_days', label: 'Streak Days' },
  { value: 'perfect_scores', label: 'Perfect Scores' },
  { value: 'goals_reached', label: 'Goals Reached' },
];

const REWARD_TYPES = [
  { value: 'cpd_points', label: 'CPD Points' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'physical', label: 'Physical Prize' },
  { value: 'recognition', label: 'Recognition' },
  { value: 'badge', label: 'Special Badge' },
];

interface MilestoneFormData {
  name: string;
  description: string;
  icon: string;
  milestone_type: string;
  target_value: number;
  reward_type: string;
  reward_title: string;
  reward_description: string;
  reward_cpd_points: number;
  is_active: boolean;
}

const defaultMilestone: MilestoneFormData = {
  name: '',
  description: '',
  icon: '🎯',
  milestone_type: 'total_sessions',
  target_value: 10,
  reward_type: 'cpd_points',
  reward_title: '',
  reward_description: '',
  reward_cpd_points: 50,
  is_active: true,
};

export function MilestoneBuilder({ companyId }: MilestoneBuilderProps) {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  const [formData, setFormData] = useState<MilestoneFormData>(defaultMilestone);

  // AI Generation state
  const { generate: aiGenerate, isLoading: aiLoading } = useAIGenerate();
  const [aiModalOpen, setAiModalOpen] = useState<'milestone_name' | 'milestone_description' | null>(null);

  const getAIContext = (): AIGenerateContext[] => {
    const context: AIGenerateContext[] = [];
    context.push({ key: 'type', label: 'Type', value: formData.milestone_type.replace(/_/g, ' ') });
    context.push({ key: 'target', label: 'Target', value: String(formData.target_value) });
    context.push({ key: 'reward', label: 'Reward', value: formData.reward_type.replace(/_/g, ' ') });
    if (formData.name && aiModalOpen === 'milestone_description') {
      context.push({ key: 'name', label: 'Milestone', value: formData.name });
    }
    return context;
  };

  const handleAIGenerate = async (customPrompt?: string): Promise<string[]> => {
    if (!aiModalOpen) return [];
    return aiGenerate({
      field: aiModalOpen,
      context: {
        milestoneType: formData.milestone_type,
        targetValue: String(formData.target_value),
        milestoneName: formData.name,
        rewardType: formData.reward_type,
        category: 'training',
      },
      customPrompt,
      variations: 3,
      category: 'gamification',
    });
  };

  const applyAIValue = (value: string) => {
    if (aiModalOpen === 'milestone_name') {
      setFormData({ ...formData, name: value });
    } else if (aiModalOpen === 'milestone_description') {
      setFormData({ ...formData, description: value });
    }
    setAiModalOpen(null);
  };

  // Fetch milestones
  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ['company-milestones', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('company_milestones')
        .select('*')
        .eq('company_id', companyId)
        .order('target_value', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!companyId,
  });

  // Create milestone
  const createMutation = useMutation({
    mutationFn: async (data: MilestoneFormData) => {
      const { error } = await supabase
        .from('company_milestones')
        .insert({
          company_id: companyId,
          ...data,
          display_order: milestones.length,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-milestones', companyId] });
      toast.success('Milestone created successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to create milestone: ' + error.message);
    },
  });

  // Update milestone
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: MilestoneFormData }) => {
      const { error } = await supabase
        .from('company_milestones')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-milestones', companyId] });
      toast.success('Milestone updated successfully');
      resetForm();
    },
    onError: (error: any) => {
      toast.error('Failed to update milestone: ' + error.message);
    },
  });

  // Delete milestone
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('company_milestones')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-milestones', companyId] });
      toast.success('Milestone deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete milestone: ' + error.message);
    },
  });

  const resetForm = () => {
    setFormData(defaultMilestone);
    setEditingMilestone(null);
    setIsDialogOpen(false);
  };

  const handleEdit = (milestone: any) => {
    setEditingMilestone(milestone);
    setFormData({
      name: milestone.name,
      description: milestone.description,
      icon: milestone.icon || '🎯',
      milestone_type: milestone.milestone_type,
      target_value: milestone.target_value,
      reward_type: milestone.reward_type,
      reward_title: milestone.reward_title || '',
      reward_description: milestone.reward_description || '',
      reward_cpd_points: milestone.reward_cpd_points || 0,
      is_active: milestone.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingMilestone) {
      updateMutation.mutate({ id: editingMilestone.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const getMilestoneLabel = (type: string) => {
    return MILESTONE_TYPES.find(t => t.value === type)?.label || type;
  };

  const getRewardLabel = (type: string) => {
    return REWARD_TYPES.find(t => t.value === type)?.label || type;
  };

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'cpd_points': return <Trophy className="h-4 w-4 text-warning" />;
      case 'certificate': return <Award className="h-4 w-4 text-primary" />;
      case 'physical': return <Gift className="h-4 w-4 text-success" />;
      case 'recognition': return <Medal className="h-4 w-4 text-accent" />;
      case 'badge': return <Medal className="h-4 w-4 text-primary" />;
      default: return <Gift className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Milestone Builder</h2>
          <p className="text-muted-foreground">Create major achievement milestones with rewards</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Milestone
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Create New Milestone'}</DialogTitle>
              <DialogDescription>
                Define the target and reward for this milestone
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-[auto_1fr] gap-4">
                <div>
                  <Label>Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(v) => setFormData({ ...formData, icon: v })}
                  >
                    <SelectTrigger className="w-20 text-2xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="grid grid-cols-5 gap-2 p-2">
                        {MILESTONE_ICONS.map(icon => (
                          <SelectItem key={icon} value={icon} className="text-2xl cursor-pointer">
                            {icon}
                          </SelectItem>
                        ))}
                      </div>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Milestone Name</Label>
                    <AIGenerateButton onClick={() => setAiModalOpen('milestone_name')} disabled={aiLoading} />
                  </div>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Session Champion"
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Description</Label>
                  <AIGenerateButton onClick={() => setAiModalOpen('milestone_description')} disabled={aiLoading} />
                </div>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What this milestone represents..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Milestone Type</Label>
                  <Select 
                    value={formData.milestone_type} 
                    onValueChange={(v) => setFormData({ ...formData, milestone_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MILESTONE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Value</Label>
                  <Input
                    type="number"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: parseInt(e.target.value) || 0 })}
                    min={1}
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Reward Configuration</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Reward Type</Label>
                    <Select 
                      value={formData.reward_type} 
                      onValueChange={(v) => setFormData({ ...formData, reward_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REWARD_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>CPD Points Bonus</Label>
                    <Input
                      type="number"
                      value={formData.reward_cpd_points}
                      onChange={(e) => setFormData({ ...formData, reward_cpd_points: parseInt(e.target.value) || 0 })}
                      min={0}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Reward Title</Label>
                  <Input
                    value={formData.reward_title}
                    onChange={(e) => setFormData({ ...formData, reward_title: e.target.value })}
                    placeholder="e.g., Gold Certificate"
                  />
                </div>

                <div className="mt-4">
                  <Label>Reward Description</Label>
                  <Input
                    value={formData.reward_description}
                    onChange={(e) => setFormData({ ...formData, reward_description: e.target.value })}
                    placeholder="e.g., Framed achievement certificate"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Active</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
                </Button>
              </div>
            </form>

            {/* AI Generation Modal */}
            {aiModalOpen && (
              <AIGenerateModal
                open={!!aiModalOpen}
                onOpenChange={(open) => !open && setAiModalOpen(null)}
                title={aiModalOpen === 'milestone_name' ? 'Milestone Name' : 'Milestone Description'}
                fieldName={aiModalOpen}
                context={getAIContext()}
                onGenerate={handleAIGenerate}
                onApply={applyAIValue}
                isLoading={aiLoading}
                multiline={aiModalOpen === 'milestone_description'}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Milestone List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-24" />
            </Card>
          ))}
        </div>
      ) : milestones.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No milestones created yet</p>
            <p className="text-sm text-muted-foreground mt-1">Create milestones to set major achievement goals</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {milestones.map((milestone: any) => (
            <Card key={milestone.id} className={!milestone.is_active ? 'opacity-60' : ''}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <span className="text-4xl">{milestone.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{milestone.name}</h3>
                      <Badge variant={milestone.is_active ? 'default' : 'secondary'}>
                        {milestone.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{milestone.description}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <div className="flex items-center gap-1 text-sm">
                        <Target className="h-4 w-4 text-primary" />
                        <span>{getMilestoneLabel(milestone.milestone_type)}: {milestone.target_value}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        {getRewardIcon(milestone.reward_type)}
                        <span>
                          {milestone.reward_title || getRewardLabel(milestone.reward_type)}
                          {milestone.reward_cpd_points > 0 && ` (+${milestone.reward_cpd_points} CPD)`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(milestone)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => deleteMutation.mutate(milestone.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
